import { Component, type ErrorInfo, type ReactNode } from 'react'
import { reportClientError } from '@/lib/errorReporting'

interface Props {
  children: ReactNode
  /** Render a full-screen fallback (root level). Default: false (inline, within the app shell) */
  fullPage?: boolean
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack)
    reportClientError({
      message: error.message,
      stack: error.stack,
      source: 'error-boundary',
      componentStack: info.componentStack ?? undefined,
    })
  }

  override render() {
    const { error } = this.state
    if (!error) return this.props.children

    if (this.props.fullPage) {
      return (
        <div style={{
          display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          background: '#f8fafc', padding: '24px',
        }}>
          <div style={{ maxWidth: 480, textAlign: 'center' }}>
            <div style={{
              width: 48, height: 48, borderRadius: 12, background: '#fee2e2',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
            }}>
              <svg width="24" height="24" fill="none" stroke="#dc2626" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>
              Algo correu mal
            </h1>
            <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 24px', lineHeight: 1.6 }}>
              Ocorreu um erro inesperado. Tente recarregar a página.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8,
                padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Recarregar página
            </button>
            {import.meta.env.DEV && (
              <pre style={{
                marginTop: 24, padding: 12, background: '#1e293b', color: '#f87171',
                borderRadius: 8, fontSize: 11, textAlign: 'left', overflowX: 'auto',
              }}>
                {error.message}
              </pre>
            )}
          </div>
        </div>
      )
    }

    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-12 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-100">
          <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-800">Erro ao carregar a página</p>
          <p className="mt-1 text-xs text-slate-500">Ocorreu um erro inesperado. Tente novamente.</p>
        </div>
        <button
          onClick={() => this.setState({ error: null })}
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700"
        >
          Tentar novamente
        </button>
        {import.meta.env.DEV && (
          <pre className="max-w-lg overflow-x-auto rounded-lg bg-slate-900 p-3 text-left text-[11px] text-red-400">
            {error.message}
          </pre>
        )}
      </div>
    )
  }
}
